import {
  assign as assignInDomain,
  closeFollowUp as closeInDomain,
  markDone,
  reopen as reopenInDomain,
  reschedule as rescheduleInDomain,
  snooze as snoozeInDomain,
  type FollowUp,
  type NewFollowUpInput,
  type ScheduleStep,
} from '../domain/followUps'
import type {
  AuthStorePort,
  ClockPort,
  CustomerNotesRepositoryPort,
  FollowUpsRepositoryPort,
  IdGeneratorPort,
} from '../ports'

/**
 * Follow-up commands.
 *
 * The scheduling arithmetic lives in `domain/followUps.ts` as pure functions;
 * this layer is only responsible for who did it, when, and for the one side
 * effect that makes follow-ups worth having: **acting on a follow-up writes
 * into the customer's thread.**
 *
 * That is the whole relationship between the two halves of this feature. A
 * follow-up is a ping — it says "talk to this person". What was actually said
 * belongs to the customer and outlives the ping, so it lands in `customer_notes`
 * tagged with the follow-up that prompted it.
 */

type ActionDeps = {
  authStore: AuthStorePort
  clock: ClockPort
  idGenerator: IdGeneratorPort
  followUpsRepo: FollowUpsRepositoryPort
  customerNotesRepo: CustomerNotesRepositoryPort
}

interface ActingUser {
  id: string
  at: string
}

function requireUser(deps: {
  authStore: AuthStorePort
  clock: ClockPort
}): ActingUser {
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }
  return { id: currentUser.id, at: deps.clock.now() }
}

/**
 * Write the outcome of a follow-up action into the customer's thread.
 *
 * Silently does nothing for an empty note — every action offers to capture one
 * and most of them decline, which is fine. The note is what survives; the
 * follow-up's own `history` only records that something happened.
 */
function recordOutcomeNote(
  followUp: FollowUp,
  note: string | undefined,
  actor: ActingUser,
  deps: Pick<ActionDeps, 'idGenerator' | 'customerNotesRepo'>,
) {
  const text = note?.trim()
  if (!text) return

  deps.customerNotesRepo.insert({
    id: deps.idGenerator.generateId(),
    customerId: followUp.customerId,
    authorId: actor.id,
    createdAt: actor.at,
    updatedAt: actor.at,
    text,
    followUpId: followUp.id,
  })
}

function requireFollowUp(
  followUpId: string,
  deps: Pick<ActionDeps, 'followUpsRepo'>,
): FollowUp {
  const followUp = deps.followUpsRepo.get(followUpId) as FollowUp | undefined
  if (!followUp) {
    throw new Error(`Follow-up ${followUpId} not found`)
  }
  return followUp
}

/**
 * Apply a pure domain transition to the stored record.
 *
 * The domain functions return a whole new follow-up; the repository wants a
 * draft mutation. Assigning the result wholesale keeps the two in step without
 * the command layer having to know which fields each transition touches.
 */
function applyTransition(
  followUpId: string,
  next: FollowUp,
  deps: Pick<ActionDeps, 'followUpsRepo'>,
) {
  deps.followUpsRepo.update(followUpId, (draft) => {
    Object.assign(draft, next)
  })
}

export function createFollowUp(
  input: NewFollowUpInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    followUpsRepo: FollowUpsRepositoryPort
  },
): FollowUp {
  const actor = requireUser(deps)

  const followUp: FollowUp = {
    id: deps.idGenerator.generateId(),
    customerId: input.customerId,
    title: input.title,
    details: input.details,
    service: input.service,
    dueAt: input.dueAt,
    schedule: input.schedule ?? [],
    scheduleIndex: 0,
    status: 'open',
    assigneeId: input.assigneeId,
    categoryId: input.categoryId,
    history: [
      { type: 'created', at: actor.at, by: actor.id, dueAt: input.dueAt },
    ],
    createdBy: actor.id,
    createdAt: actor.at,
    updatedAt: actor.at,
  }

  deps.followUpsRepo.insert(followUp)

  return followUp
}

/**
 * Record that the follow-up was acted on.
 *
 * With a schedule this re-arms rather than ends — see `markDone`. The returned
 * follow-up tells the caller which happened, so the UI can say "done, back in
 * 6 months" instead of implying it is gone.
 */
export function completeFollowUp(
  followUpId: string,
  options: { note?: string },
  deps: ActionDeps,
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = markDone(followUp, { at: actor.at, by: actor.id, note: options.note })

  applyTransition(followUpId, next, deps)
  recordOutcomeNote(followUp, options.note, actor, deps)

  return next
}

/** Push the follow-up out without touching its schedule. */
export function snoozeFollowUp(
  followUpId: string,
  step: ScheduleStep,
  options: { note?: string },
  deps: ActionDeps,
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = snoozeInDomain(followUp, step, {
    at: actor.at,
    by: actor.id,
    note: options.note,
  })

  applyTransition(followUpId, next, deps)
  recordOutcomeNote(followUp, options.note, actor, deps)

  return next
}

/** Move the due date to a specific day, leaving the schedule alone. */
export function rescheduleFollowUp(
  followUpId: string,
  dueAt: string,
  options: { note?: string },
  deps: ActionDeps,
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = rescheduleInDomain(followUp, dueAt, {
    at: actor.at,
    by: actor.id,
    note: options.note,
  })

  applyTransition(followUpId, next, deps)
  recordOutcomeNote(followUp, options.note, actor, deps)

  return next
}

/**
 * End the follow-up for good.
 *
 * The deliberate counterweight to `completeFollowUp`: this is the only action
 * that stops a scheduled follow-up coming back, so it is always an explicit
 * choice rather than a side effect of finishing a task.
 */
export function closeFollowUp(
  followUpId: string,
  options: { note?: string },
  deps: ActionDeps,
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = closeInDomain(followUp, {
    at: actor.at,
    by: actor.id,
    note: options.note,
  })

  applyTransition(followUpId, next, deps)
  recordOutcomeNote(followUp, options.note, actor, deps)

  return next
}

/** Hand the follow-up to someone. No note — assignment isn't an outcome. */
export function assignFollowUp(
  followUpId: string,
  assigneeId: string,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    followUpsRepo: FollowUpsRepositoryPort
  },
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = assignInDomain(followUp, assigneeId, {
    at: actor.at,
    by: actor.id,
  })

  applyTransition(followUpId, next, deps)

  return next
}

/** Bring a closed follow-up back with a fresh due date. */
export function reopenFollowUp(
  followUpId: string,
  dueAt: string,
  options: { note?: string },
  deps: ActionDeps,
): FollowUp {
  const actor = requireUser(deps)
  const followUp = requireFollowUp(followUpId, deps)

  const next = reopenInDomain(followUp, dueAt, {
    at: actor.at,
    by: actor.id,
    note: options.note,
  })

  applyTransition(followUpId, next, deps)
  recordOutcomeNote(followUp, options.note, actor, deps)

  return next
}

/**
 * Edit what the follow-up says, not when it fires.
 *
 * Scheduling changes go through the transitions above so they land in
 * `history`; this is for fixing a typo or attaching the job it turned out to
 * be about.
 */
export function updateFollowUpDetails(
  followUpId: string,
  patch: Partial<
    Pick<FollowUp, 'title' | 'details' | 'service' | 'schedule' | 'categoryId'>
  >,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    followUpsRepo: FollowUpsRepositoryPort
  },
) {
  const actor = requireUser(deps)

  deps.followUpsRepo.update(followUpId, (draft) => {
    Object.assign(draft, patch)
    // A replacement ladder starts from its first rung — editors show the
    // *remaining* rungs (see remainingSchedule) and save the whole edited
    // list, so a stale index would point into the wrong ladder.
    if (patch.schedule) draft.scheduleIndex = 0
    draft.updatedAt = actor.at
  })
}

/**
 * Hard-delete a follow-up.
 *
 * For one raised by mistake. A follow-up that ran its course should be closed,
 * not deleted — closing keeps its history and any notes it prompted.
 */
export function discardFollowUp(
  followUpId: string,
  deps: { followUpsRepo: FollowUpsRepositoryPort },
) {
  deps.followUpsRepo.delete(followUpId)
}
